/**
 * OneSignal Push Notification Service
 * Handles initialization, user linking, and preference management
 */

import OneSignal from 'react-onesignal';

// Track initialization state
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Initialize OneSignal SDK
 * Should be called once when the app starts
 */
export async function initializeOneSignal(): Promise<void> {
  if (isInitialized) {
    console.log('OneSignal already initialized');
    return;
  }

  // Return existing promise if initialization is in progress
  if (initializationPromise) {
    return initializationPromise;
  }

  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  const safariWebId = import.meta.env.VITE_ONESIGNAL_SAFARI_WEB_ID;

  console.log('OneSignal init config:', {
    appId: appId ? `${appId.slice(0, 8)}...` : 'MISSING',
    safariWebId: safariWebId ? `${safariWebId.slice(0, 20)}...` : 'not set',
    isDev: import.meta.env.DEV,
    origin: window.location.origin,
  });

  if (!appId) {
    console.warn(
      'OneSignal App ID not configured. Push notifications disabled.'
    );
    return;
  }

  initializationPromise = (async () => {
    try {
      await OneSignal.init({
        appId,
        safari_web_id: safariWebId,
        serviceWorkerParam: { scope: '/onesignal/' },
        serviceWorkerPath: 'onesignal/OneSignalSDKWorker.js',
        allowLocalhostAsSecureOrigin: import.meta.env.DEV,
      });

      isInitialized = true;
      console.log('OneSignal initialized successfully');
    } catch (error) {
      console.error('Failed to initialize OneSignal:', error);
    }
  })();

  return initializationPromise;
}

/**
 * Wait for OneSignal to be initialized
 * Use this before checking permission status to avoid race conditions
 */
export async function waitForOneSignalInit(): Promise<boolean> {
  if (isInitialized) {
    return true;
  }

  if (initializationPromise) {
    await initializationPromise;
    return isInitialized;
  }

  // OneSignal was never initialized (e.g., no app ID configured)
  return false;
}

/**
 * Link OneSignal user to Supabase auth user
 * Call this when user logs in
 */
export async function loginOneSignalUser(userId: string): Promise<void> {
  if (!isInitialized) {
    console.warn('OneSignal not initialized');
    return;
  }

  try {
    await OneSignal.login(userId);
    console.log('OneSignal user logged in:', userId);
  } catch (error) {
    console.error('Failed to login OneSignal user:', error);
  }
}

/**
 * Logout OneSignal user
 * Call this when user logs out
 */
export async function logoutOneSignalUser(): Promise<void> {
  if (!isInitialized) {
    return;
  }

  try {
    await OneSignal.logout();
    console.log('OneSignal user logged out');
  } catch (error) {
    console.error('Failed to logout OneSignal user:', error);
  }
}

/**
 * Request push notification permission from the user
 * Returns true if permission was granted
 */
export async function requestNotificationPermission(): Promise<boolean> {
  // Try to initialize if not already done
  if (!isInitialized) {
    console.warn('OneSignal not initialized, attempting initialization...');
    await initializeOneSignal();

    if (!isInitialized) {
      console.error(
        'OneSignal failed to initialize - cannot request permission'
      );
      return false;
    }
  }

  try {
    console.log('Requesting notification permission...');
    await OneSignal.Notifications.requestPermission();
    const permission = await OneSignal.Notifications.permission;
    console.log('Permission result:', permission);
    return permission;
  } catch (error) {
    console.error('Failed to request notification permission:', error);
    return false;
  }
}

/**
 * Check if push notifications are enabled
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!isInitialized) {
    return false;
  }

  try {
    return await OneSignal.Notifications.permission;
  } catch (error) {
    console.error('Failed to check push permission:', error);
    return false;
  }
}

/**
 * Notification preference tags that map to Supabase notification_preferences
 */
export interface NotificationTags {
  notif_event_reminders: 'on' | 'off';
  notif_event_changes: 'on' | 'off';
}

/**
 * Update global notification preference tags in OneSignal
 * These tags are used for targeting notifications
 */
export async function updateNotificationTags(
  tags: Partial<NotificationTags>
): Promise<void> {
  if (!isInitialized) {
    console.warn('OneSignal not initialized');
    return;
  }

  try {
    await OneSignal.User.addTags(tags);
    console.log('OneSignal tags updated:', tags);
  } catch (error) {
    console.error('Failed to update OneSignal tags:', error);
  }
}

/**
 * Convert boolean preferences to OneSignal tag format
 */
export function preferencesToTags(preferences: {
  notif_event_reminders: boolean;
  notif_event_changes: boolean;
}): NotificationTags {
  return {
    notif_event_reminders: preferences.notif_event_reminders ? 'on' : 'off',
    notif_event_changes: preferences.notif_event_changes ? 'on' : 'off',
  };
}

/**
 * Get current notification tags from OneSignal
 */
export async function getNotificationTags(): Promise<Partial<NotificationTags> | null> {
  if (!isInitialized) {
    return null;
  }

  try {
    const tags = await OneSignal.User.getTags();
    return tags as Partial<NotificationTags>;
  } catch (error) {
    console.error('Failed to get OneSignal tags:', error);
    return null;
  }
}
